import { inputValueToUnix } from '@/shared/lib/datetime';
import type { SortOrder } from '@/shared/lib/useListFilters';
import type {
  FilterValue,
  FilterValues,
  ListFilter,
  ListPageConfig,
  ListParams,
  ListRow,
} from './types';

const isEmptyValue = (value: FilterValue | undefined) =>
  Array.isArray(value) ? value.length === 0 : !value?.trim();

const emptyValue = (filter: ListFilter): FilterValue => {
  const { defaultValue } = filter;
  if (defaultValue !== undefined) return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
  return filter.kind === 'multiselect' ? [] : '';
};

/** Initial (cleared) draft/applied state: search key + value and every configured filter. */
export const buildEmptyFilters = <T extends ListRow>(config: ListPageConfig<T>): FilterValues => ({
  searchKey: '',
  searchValue: '',
  ...Object.fromEntries((config.filters ?? []).map((f) => [f.name, emptyValue(f)])),
});

export const hasAppliedFilter = (applied: FilterValues) =>
  Object.entries(applied).some(([key, value]) => key !== 'searchKey' && !isEmptyValue(value));

type RequestView = { applied: FilterValues; page: number; sortField: string; sortOrder: SortOrder };

/** Flat API params: `baseParams`, page, sort, the selected search key only, and non-client filters. */
export function buildListParams<T extends ListRow>(
  config: ListPageConfig<T>,
  { applied, page, sortField, sortOrder }: RequestView,
): ListParams {
  const params: ListParams = { ...config.baseParams };

  if (config.paginated !== false) params.page = page;

  if (sortField) {
    if (config.sortStyle === 'signed') {
      params.sort = sortOrder === 'desc' ? `-${sortField}` : sortField;
    } else {
      params.sort_field = sortField;
      params.sort_order = sortOrder;
    }
  }

  config.searchKeys?.forEach(({ value: key }) => {
    params[key] = key === applied.searchKey ? String(applied.searchValue) : '';
  });

  config.filters
    ?.filter((filter) => !filter.clientOnly)
    .forEach((filter) => {
      const value = applied[filter.name];
      params[filter.name] =
        filter.kind === 'datetime' ? inputValueToUnix(String(value)) : value;
    });

  return params;
}
